import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../../../../../enumStrings";
import {
  useCreateChamberStaffUserMutation,
} from "../../../../../graphql/types";
import UnsavedChangesPrompt from "../../../../common/UnsavedChangesPrompt";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { usePageHeaderOverride } from "../../../../Layout/PageContainer";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import StaffUserForm, { EMPLOYEE_FORM_INTRO_TEXT } from "./StaffUserForm";
import {
  EMPTY_STAFF_USER_FORM_VALUE,
  StaffUserFormValue,
} from "./types";
import { isManageableChamberStaffUser } from "./staffUserIdentity";
import { getOperationErrorMessage, PRESERVE_ERROR_FOR_CALLER_CONTEXT } from "./uiUtilities";
import { UserFacingError } from "../../../../../utils/userFacingError";
import { getStaffUserCreationFeedback } from "./staffUserCreationFeedback";
import { toChamberStaffUsagePermissionsInput } from "./staffUserMutationInputs";

// The account may have been created, so staff check the list first.
const STAFF_CREATE_UNCONFIRMED_TEXT =
  "We could not confirm that the employee account was created. Go back to Create Employees and Permissions and check the list before you try again.";

const USERS_PERMISSIONS_TAB_QUERY = "tab=usersPermissions";

export const getUsersPermissionsReturnPath = (): string =>
  `${RoutePaths.chamberAdmin_myChamber_chamberSettings}?${USERS_PERMISSIONS_TAB_QUERY}`;

export const CreateStaffUserPage = () => {
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const noun = useCmpOrganizationNoun();
  const [isDirty, setIsDirty] = useState(false);
  const [creationCommitted, setCreationCommitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(
    null
  );
  const [createStaffUser, { loading }] = useCreateChamberStaffUserMutation({
    context: PRESERVE_ERROR_FOR_CALLER_CONTEXT,
  });

  useEffect(() => {
    // The design places user creation inside Chamber Set Up (D:5219,
    // D:6886-6936), so this dedicated page keeps that header.
    setPageHeaderOverride({
      title: `${noun.Singular} Set Up`,
      description: `Set your ${noun.singular} membership model, payment options, approval process, email automation.`,
    });

    return () => setPageHeaderOverride(null);
  }, [noun, setPageHeaderOverride]);

  const handleDirtyChange = useCallback((nextIsDirty: boolean) => {
    setIsDirty(nextIsDirty);
    if (nextIsDirty) {
      setSubmitError("");
    }
  }, []);

  const handleSubmit = async (value: StaffUserFormValue): Promise<void> => {
    setSubmitError("");

    try {
      const result = await createStaffUser({
        variables: {
          input: {
            firstName: value.firstName,
            lastName: value.lastName,
            jobTitle: value.jobTitle || null,
            email: value.email,
            usagePermissions: toChamberStaffUsagePermissionsInput({
              selection: value.permissions,
            }),
          },
        },
      });
      const response = result.data?.createChamberStaffUser;

      if (!response) {
        throw new UserFacingError(STAFF_CREATE_UNCONFIRMED_TEXT);
      }

      const returnedStaffIdentityIsValid = isManageableChamberStaffUser(
        response.staffUser
      );
      const feedback = getStaffUserCreationFeedback({
        displayName: `${value.firstName} ${value.lastName}`,
        deliveryStatus: returnedStaffIdentityIsValid
          ? response.credentialDeliveryStatus
          : "UNKNOWN",
      });

      // A non-null mutation payload means account creation committed. Never
      // turn an unrecognized delivery/identity response into a generic failure
      // that invites a duplicate retry; return to the list with UNKNOWN status.
      setCreationCommitted(true);
      navigate(getUsersPermissionsReturnPath(), {
        state: { staffUserCreationFeedback: feedback },
      });
    } catch (operationError) {
      const message = getOperationErrorMessage(
        operationError,
        STAFF_CREATE_UNCONFIRMED_TEXT
      );

      setSubmitError(message);
      setErrorDialogMessage(message);
    }
  };

  return (
    <>
      <UnsavedChangesPrompt
        when={isDirty && !creationCommitted}
        saving={loading}
      />
      <ActionSuccessDialog
        message={errorDialogMessage}
        tone="error"
        onClose={() => setErrorDialogMessage(null)}
      />

      <main className="mx-auto flex w-full max-w-[1180px] flex-col gap-[24px] pb-10 pt-4">
        <div>
          <h2
            id="create-staff-user-heading"
            className="text-[18px] font-semibold text-[#0A0A0A]"
          >
            Create Employees and Permissions
          </h2>
          <p className="mt-[4px] text-[14px] text-[#0A0A0A]">
            {EMPLOYEE_FORM_INTRO_TEXT}
          </p>
        </div>
        <section
          aria-labelledby="create-staff-user-heading"
          className="rounded-[12px] border border-[#ECECEC] bg-white p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
        >
          <StaffUserForm
            mode="create"
            initialValue={EMPTY_STAFF_USER_FORM_VALUE}
            onSubmit={handleSubmit}
            cancelPath={getUsersPermissionsReturnPath()}
            onDirtyChange={handleDirtyChange}
            loading={loading}
            submitError={submitError}
          />
        </section>
      </main>
    </>
  );
};

export default CreateStaffUserPage;
